import { CameraOutlined } from '@ant-design/icons';
import { Badge } from 'antd';
import { BuyModal } from 'components/Modals/BuyModal';
import { Section } from 'components/UI/ProductCard/atoms';
import {
  ActionButtons,
  OfferInfo,
  PriceInfo,
  ProductHeader,
  QualityInfo,
} from 'components/UI/ProductCard/molecules';
import ProductCardImage from 'components/UI/ProductCardImage';
import { RealPhotosLegend } from 'components/UI/ProductCardImageSimple.styled';
import { useCompositionButton } from 'hooks/useCompositionButton';
import { useShippingFee } from 'hooks/useShippingFee';
import { useCallback, useMemo, useState } from 'react';
import { isDesktop, isTablet } from 'react-device-detect';
import {
  ProductCardListCard,
  ProductCardListContainer,
} from './ProductCardList.styled';
import { ProductCardListProps } from './types';

export function ProductCardListMobile({
  product,
  packingSelected,
  getLowPrice,
  onDetailsClick,
  disabled = false,
  addInCart,
  selectedTypeSale,
  buy,
  showModalComposition,
  onSelectPacking,
  isExclusiveOffer = false,
}: ProductCardListProps) {
  const [buyModalOpen, setBuyModalOpen] = useState(false);
  const [addToCartModalOpen, setAddToCartModalOpen] = useState(false);

  const canShowComposition = useCompositionButton(product);
  const { shippingFee } = useShippingFee(product);

  const handleBuyClick = useCallback(() => {
    setBuyModalOpen(true);
  }, []);

  const handleBuyModalClose = useCallback(() => {
    setBuyModalOpen(false);
  }, []);

  const handleAddToCartModalOpen = useCallback(() => {
    setAddToCartModalOpen(true);
  }, []);

  const handleAddToCartModalClose = useCallback(() => {
    setAddToCartModalOpen(false);
  }, []);

  const handleBuyConfirm = useCallback(
    (purchaseData) => {
      setBuyModalOpen(false);
      if (buy) {
        const { quantity, purchaseType } = purchaseData;
        buy(quantity, purchaseType);
      }
    },
    [buy],
  );

  const handleAddToCartConfirm = useCallback(
    (purchaseData) => {
      setAddToCartModalOpen(false);
      if (addInCart) {
        const { quantity, purchaseType } = purchaseData;
        addInCart(quantity, purchaseType);
      }
    },
    [addInCart],
  );

  const handleAddToCart = useCallback(() => {
    if (!addInCart || !selectedTypeSale) {
      return;
    }

    handleAddToCartModalOpen();
  }, [addInCart, selectedTypeSale, handleAddToCartModalOpen]);

  const { offerType } = product;
  const isLkpOffer = useMemo(
    () => offerType === 1 || offerType === 2,
    [offerType],
  );

  const detailsButtonText = useMemo(() => {
    if (!isTablet) {
      return 'Abrir';
    }

    if (isTablet && !isDesktop) {
      return isLkpOffer ? 'Detalhes | GFPS' : 'Detalhes';
    }

    return isLkpOffer ? 'Detalhes | Comprar por GFP' : 'Detalhes';
  }, [isLkpOffer, isTablet, isDesktop]);

  const isDirectedOffer = product.offerType === 3;
  const hasSpecialPrice =
    !Number.isNaN(product.specialPrice) && product.specialPrice > 0;
  const hasValidReajustment =
    !Number.isNaN(product.reajustmentAmount) && product.reajustmentAmount !== 0;

  const shouldShowBadge = isDirectedOffer
    ? hasSpecialPrice
    : hasValidReajustment;

  let badgeText = '';
  if (isDirectedOffer && hasSpecialPrice) {
    badgeText = `Oferta direcionada R$${Math.abs(product.specialPrice)
      .toFixed(2)
      .replace('.', ',')}`;
  } else if (product.voucherReajustmentType === 'P') {
    badgeText = `${Math.abs(product.reajustmentAmount)}%`;
  } else {
    badgeText = `R$${Math.abs(product.reajustmentAmount)
      .toFixed(2)
      .replace('.', ',')}`;
  }

  return (
    <ProductCardListContainer className="product-card-list-mobile-container">
      <Badge.Ribbon
        text={badgeText}
        placement="start"
        color="green"
        style={{ display: shouldShowBadge ? 'block' : 'none' }}
      >
        <ProductCardListCard>
          {product.imagesFormatted?.[0] && (
            <ProductCardImage
              images={product.imagesFormatted}
              alt={`Imagem de ${product.longName}`}
              productName={product.longName}
              isLkpOffer={isLkpOffer}
              showNavigation
              showLabel={false}
            />
          )}
          <ProductHeader
            product={product}
            isExclusiveOffer={isExclusiveOffer}
          />
          <OfferInfo product={product} />
          {(product.quality || packingSelected?.description) && (
            <Section withTopBorder marginTop={8} paddingTop={8}>
              <QualityInfo
                product={product}
                packingSelected={packingSelected}
                onSelectPacking={onSelectPacking}
              />
            </Section>
          )}
          {isLkpOffer &&
            (Array.isArray(product.gfpImages)
              ? product.gfpImages.length > 0
              : !!product.gfpImages) && (
              <RealPhotosLegend>
                <CameraOutlined />
                Oferta com fotos reais. Veja nos detalhes (GFPs).
              </RealPhotosLegend>
            )}

          <Section withTopBorder marginTop={8} paddingTop={8}>
            <PriceInfo price={getLowPrice} unit="un" shipping={shippingFee} />
          </Section>
          <ActionButtons
            onDetailsClick={onDetailsClick}
            onBuyClick={handleBuyClick}
            onAddToCart={handleAddToCart}
            onCompositionClick={showModalComposition}
            showCompositionButton={canShowComposition}
            disabled={disabled}
            disableBuyButton={!isLkpOffer}
            buyText="Comprar agora"
            detailsTextMobile={detailsButtonText}
          />
        </ProductCardListCard>
      </Badge.Ribbon>
      {buyModalOpen && (
        <BuyModal
          key={packingSelected?.id}
          visible={buyModalOpen}
          onCancel={handleBuyModalClose}
          onConfirm={handleBuyConfirm}
          product={product}
          variant="buy"
          packingSelected={packingSelected}
        />
      )}
      {addToCartModalOpen && (
        <BuyModal
          key={packingSelected?.id}
          visible={addToCartModalOpen}
          onCancel={handleAddToCartModalClose}
          onConfirm={handleAddToCartConfirm}
          product={product}
          variant="addToCart"
          packingSelected={packingSelected}
        />
      )}
    </ProductCardListContainer>
  );
}
